/**
 * Mobile bottom sheet for dimension dropdown variant selects.
 */
if (!customElements.get('size-option-sheet')) {
  class SizeOptionSheet extends HTMLElement {
    constructor() {
      super();
      this.mql = window.matchMedia('(min-width: 750px)');
      this._selectedValue = '';
      this.boundMqlChange = this.onMqlChange.bind(this);
      this.boundSelectChange = this.onSelectChange.bind(this);
      this.boundDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.boundOptionActivate = this.onOptionActivate.bind(this);
    }

    connectedCallback() {
      this.select = this.querySelector('select');
      this.trigger = this.querySelector('.size-option-sheet__trigger');
      this.triggerValue = this.querySelector('.size-option-sheet__trigger-value');
      this.panel = this.querySelector('.size-option-sheet__panel');
      this.overlay = this.querySelector('.size-option-sheet__overlay');
      this.list = this.querySelector('.size-option-sheet__list');
      this.closeBtn = this.querySelector('.size-option-sheet__close');
      this.optionPosition = parseInt(this.dataset.optionPosition, 10);

      const variantsJson = this.querySelector('[data-size-sheet-variants]');
      this.variants = variantsJson ? JSON.parse(variantsJson.textContent) : [];

      this.trigger?.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      this.list?.addEventListener('click', this.boundOptionActivate);
      this.list?.addEventListener('pointerup', this.boundOptionActivate);
      this.select?.addEventListener('change', this.boundSelectChange);

      this.mql.addEventListener('change', this.boundMqlChange);
      this.updateMode();
      this.hasUserSelected = false;
      this.ensurePlaceholderSelected(false);

      if (new URLSearchParams(window.location.search).get('variant')) {
        this.restoreFromFormVariantId(true);
      }

      this.syncFromSelect();
      this.updateListPrices();

      if (typeof subscribe === 'function') {
        this.optionChangeUnsub = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
          this.updateListPrices();
          this.syncFromSelect();
        });

        this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const productInfo = this.closest('product-info');
          if (!productInfo || String(data?.sectionId) !== String(productInfo.dataset.section)) return;

          requestAnimationFrame(() => {
            if (this.hasUserSelected) {
              this.restoreFromFormVariantId();
            }
            this.syncFromSelect();
            this.updateListPrices();
          });
        });
      }
    }

    disconnectedCallback() {
      this.mql.removeEventListener('change', this.boundMqlChange);
      this.select?.removeEventListener('change', this.boundSelectChange);
      this.list?.removeEventListener('click', this.boundOptionActivate);
      this.list?.removeEventListener('pointerup', this.boundOptionActivate);
      document.removeEventListener('keydown', this.boundDocumentKeydown);
      document.body.classList.remove('size-option-sheet--open');
      this.optionChangeUnsub?.();
      this.variantChangeUnsub?.();
    }

    onMqlChange() {
      this.updateMode();
      if (this.mql.matches) this.close(false);
    }

    updateMode() {
      const isMobile = !this.mql.matches;
      this.classList.toggle('size-option-sheet--mobile', isMobile);
      this.#syncPlaceholderDisabledState(isMobile);
    }

    #syncPlaceholderDisabledState(isMobile) {
      const placeholder = this.select?.querySelector('option[data-placeholder]');
      if (!placeholder) return;

      // iOS Safari often ignores select.value changes while a disabled option stays selected.
      if (isMobile) {
        placeholder.removeAttribute('disabled');
      } else {
        placeholder.setAttribute('disabled', 'disabled');
      }
    }

    getVariantOptionValue(variant, position) {
      if (!variant) return null;
      if (Array.isArray(variant.options) && variant.options[position - 1] != null) {
        return variant.options[position - 1];
      }
      return variant[`option${position}`] || null;
    }

    getOtherSelectedOptions() {
      const variantSelects = this.closest('variant-selects');
      if (!variantSelects) return {};

      const selections = {};
      variantSelects.querySelectorAll('size-option-sheet').forEach((sheet) => {
        const pos = parseInt(sheet.dataset.optionPosition, 10);
        if (pos === this.optionPosition) return;
        const val = sheet._selectedValue || sheet.select?.value;
        if (val) selections[pos] = val;
      });

      variantSelects.querySelectorAll('select:not(.size-option-sheet__select)').forEach((select) => {
        const name = select.getAttribute('name') || '';
        const match = name.match(/options\[(.+)\]/);
        if (!match || !select.value) return;
        const pos = this.getSelectPosition(variantSelects, select);
        if (pos && pos !== this.optionPosition) selections[pos] = select.value;
      });

      variantSelects.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
        const fieldset = input.closest('fieldset');
        if (!fieldset) return;
        const pos = this.getFieldsetPosition(variantSelects, fieldset);
        if (pos && pos !== this.optionPosition) selections[pos] = input.value;
      });

      return selections;
    }

    getSelectPosition(variantSelects, select) {
      const sheet = select.closest('size-option-sheet');
      if (sheet) return parseInt(sheet.dataset.optionPosition, 10);
      const selects = [...variantSelects.querySelectorAll('select')];
      return selects.indexOf(select) + 1;
    }

    getFieldsetPosition(variantSelects, fieldset) {
      const fieldsets = [...variantSelects.querySelectorAll('fieldset')];
      return fieldsets.indexOf(fieldset) + 1;
    }

    findVariantForValue(value) {
      const others = this.getOtherSelectedOptions();

      const exact = this.variants.find((variant) => {
        if (this.getVariantOptionValue(variant, this.optionPosition) !== value) return false;
        for (const [pos, val] of Object.entries(others)) {
          if (this.getVariantOptionValue(variant, parseInt(pos, 10)) !== val) return false;
        }
        return true;
      });

      if (exact) return exact;

      return this.variants.find(
        (variant) => this.getVariantOptionValue(variant, this.optionPosition) === value
      );
    }

    allDimensionSheetsSelected() {
      const variantSelects = this.closest('variant-selects');
      if (!variantSelects) return true;

      const sheets = variantSelects.querySelectorAll('size-option-sheet');
      if (!sheets.length) return true;

      return [...sheets].every((sheet) => !sheet.isPlaceholderSelected());
    }

    getSelectedValue() {
      if (this.isPlaceholderSelected()) return null;
      return this._selectedValue || this.select?.value || null;
    }

    syncVariantIdToForm() {
      if (this.isPlaceholderSelected()) return;
      if (!this.allDimensionSheetsSelected()) return;

      const value = this._selectedValue || this.select?.value;
      if (!value) return;

      const variant = this.findVariantForValue(value);
      if (!variant?.id) return;

      const productForm = this.closest('product-info')?.querySelector('product-form');
      const input = productForm?.querySelector('input[name="id"]');
      if (!input) return;

      input.value = String(variant.id);
      input.disabled = !variant.available;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    restoreFromFormVariantId(force = false) {
      if (!force && !this.hasUserSelected) return;

      const productForm = this.closest('product-info')?.querySelector('product-form');
      const variantId = productForm?.querySelector('input[name="id"]')?.value;
      if (!variantId || !this.variants.length || !this.select) return;

      const variant = this.variants.find((entry) => String(entry.id) === String(variantId));
      if (!variant) return;

      const optionValue = this.getVariantOptionValue(variant, this.optionPosition);
      if (!optionValue) return;

      const matchingOption = Array.from(this.select.options).find(
        (option) => option.value === optionValue && option.dataset.placeholder !== 'true'
      );
      if (!matchingOption) return;

      this.hasUserSelected = true;
      this.selectOptionByValue(optionValue);
    }

    formatMoney(cents) {
      if (typeof theme === 'undefined' || !theme.Currency?.formatMoney) {
        const amount = cents / 100;
        const precision = Number.isInteger(amount) ? 0 : 2;
        const fixed = amount.toFixed(precision);
        const [intPart, fracPart] = fixed.split('.');
        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

        return fracPart ? `${formattedInt},${fracPart}` : formattedInt;
      }

      const baseFormat = theme.shopSettings?.moneyFormat || '{{amount}}';
      const amountTemplate =
        cents % 100 === 0
          ? '{{amount_no_decimals_with_comma_separator}}'
          : '{{amount_with_comma_separator}}';
      const panelFormat = baseFormat.replace(/\{\{\s*\w+\s*\}\}/, amountTemplate);

      return theme.Currency.formatMoney(cents, panelFormat);
    }

    ensurePlaceholderSelected(dispatchChange = false) {
      const placeholderOption = this.select?.querySelector('option[data-placeholder]');
      if (!placeholderOption || this.hasUserSelected) return;

      this._selectedValue = '';
      Array.from(this.select.options).forEach((option) => {
        option.selected = false;
        option.removeAttribute('selected');
      });
      this.select.selectedIndex = 0;
      placeholderOption.selected = true;
      placeholderOption.setAttribute('selected', 'selected');

      if (dispatchChange) {
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    isPlaceholderSelected() {
      if (this.hasUserSelected && this._selectedValue) return false;
      if (!this.select) return true;

      const selectedOption = this.getSelectedOption();
      return !selectedOption || selectedOption.dataset.placeholder === 'true';
    }

    getPlaceholderLabel() {
      return this.dataset.placeholderLabel || 'Boyut Seçiniz';
    }

    updateSubmitState() {
      const productForm = this.closest('product-info')?.querySelector('product-form');

      if (!this.isPlaceholderSelected()) {
        productForm?.handleErrorMessage();
      }

      productForm?.applySubmitButtonState();
    }

    updateListPrices() {
      this.list?.querySelectorAll('.size-option-sheet__option').forEach((btn) => {
        const value = btn.getAttribute('data-value');
        const variant = this.findVariantForValue(value);
        let priceEl = btn.querySelector('.size-option-sheet__option-price');

        if (variant) {
          btn.dataset.variantPrice = String(variant.price);
          if (!priceEl) {
            priceEl = document.createElement('span');
            priceEl.className = 'size-option-sheet__option-price';
            btn.appendChild(priceEl);
          }
          priceEl.innerHTML = `<price-money><bdi>${this.formatMoney(variant.price)}</bdi></price-money>`;
          btn.disabled = !variant.available;
          btn.closest('.size-option-sheet__item')?.classList.toggle('size-option-sheet__item--disabled', !variant.available);
        }
      });
    }

    onSelectChange() {
      this.syncSelectedValueFromNative();
      this.syncFromSelect();
    }

    syncSelectedValueFromNative() {
      if (!this.select) return;

      const selectedOption = this.select.options[this.select.selectedIndex];

      if (!selectedOption || selectedOption.dataset.placeholder === 'true') {
        this._selectedValue = '';
        return;
      }

      this._selectedValue = selectedOption.value;
      this.hasUserSelected = true;
    }

    syncFromSelect() {
      if (!this.select || !this.triggerValue) return;

      const isPlaceholder = this.isPlaceholderSelected();
      const selectedOption = isPlaceholder ? null : this.getSelectedOption();
      const value = isPlaceholder ? '' : (this._selectedValue || selectedOption?.value || '');
      const label = isPlaceholder
        ? this.getPlaceholderLabel()
        : (selectedOption?.textContent?.trim() || value);

      this.triggerValue.setAttribute('data-size-value', value);
      this.triggerValue.textContent = label;
      this.triggerValue.classList.toggle('size-option-sheet__trigger-value--placeholder', isPlaceholder);

      this.list?.querySelectorAll('.size-option-sheet__option').forEach((btn) => {
        const isSelected = !isPlaceholder && btn.getAttribute('data-value') === value;
        btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        btn.closest('.size-option-sheet__item')?.classList.toggle('size-option-sheet__item--selected', isSelected);
      });

      this.updateSubmitState();
      if (!isPlaceholder) {
        this.hasUserSelected = true;
        this.syncVariantIdToForm();
      }

      window.YarisSizeUnits?.applyAll?.();
    }

    open() {
      if (this.mql.matches || !this.panel) return;

      this.updateListPrices();
      this.panel.hidden = false;
      this.overlay.hidden = false;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
      });

      this.trigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('size-option-sheet--open');
      document.addEventListener('keydown', this.boundDocumentKeydown);
      window.YarisSizeUnits?.applyAll?.();
    }

    close(animate = true) {
      if (!this.panel) return;

      this.classList.remove('is-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('size-option-sheet--open');
      document.removeEventListener('keydown', this.boundDocumentKeydown);

      const hide = () => {
        this.panel.hidden = true;
        if (this.overlay) this.overlay.hidden = true;
      };

      if (animate) {
        setTimeout(hide, 350);
      } else {
        hide();
      }
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    getSelectedOption() {
      if (!this.select) return null;

      const nativeSelected = this.select.options[this.select.selectedIndex];
      if (
        nativeSelected &&
        nativeSelected.dataset.placeholder !== 'true' &&
        this._selectedValue &&
        this._selectedValue !== nativeSelected.value
      ) {
        this._selectedValue = nativeSelected.value;
      }

      if (this._selectedValue) {
        const matched = Array.from(this.select.options).find(
          (option) => option.value === this._selectedValue && option.dataset.placeholder !== 'true'
        );
        if (matched) return matched;
      }

      const selectedOption = this.select.options[this.select.selectedIndex];
      if (selectedOption?.dataset.placeholder === 'true') return null;

      return selectedOption || null;
    }

    getSelectedOptionValueId() {
      return this.getSelectedOption()?.dataset.optionValueId || null;
    }

    findOptionByValue(value) {
      if (!value || !this.select) return null;

      return Array.from(this.select.options).find(
        (option) => option.value === value && option.dataset.placeholder !== 'true'
      );
    }

    selectOptionByValue(value) {
      const matchingOption = this.findOptionByValue(value);
      if (!matchingOption || !this.select) return false;

      this._selectedValue = value;
      this.hasUserSelected = true;

      Array.from(this.select.options).forEach((option) => {
        option.selected = false;
        option.removeAttribute('selected');
      });

      matchingOption.selected = true;
      matchingOption.setAttribute('selected', 'selected');
      this.select.selectedIndex = matchingOption.index;

      // Setting .value after selectedIndex helps WebKit keep the native select in sync.
      try {
        this.select.value = value;
      } catch (error) {
        // Ignore — selectedIndex is authoritative for the sheet UI.
      }

      if (this.select.value !== value) {
        this.select.selectedIndex = matchingOption.index;
      }

      return true;
    }

    onOptionActivate(event) {
      if (event.type === 'pointerup') {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this._handledByPointer = true;
        window.setTimeout(() => {
          this._handledByPointer = false;
        }, 500);
      } else if (this._handledByPointer) {
        return;
      }

      const btn = event.target.closest('.size-option-sheet__option');
      if (!btn || btn.disabled) return;

      const value = btn.getAttribute('data-value');
      if (!value || !this.select) return;
      if (!this.selectOptionByValue(value)) return;

      event.preventDefault();
      this.syncFromSelect();
      this.close(false);

      requestAnimationFrame(() => {
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  customElements.define('size-option-sheet', SizeOptionSheet);
}
